import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, fmtDate } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  Map, Plus, Trash2, X, History, Download, Search, ListChecks, PlayCircle,
  AlertTriangle, CheckCircle2, FileText, Code2, FlaskConical, Rocket,
  Calendar, Gauge, Flag, Activity, ArrowRight,
} from "lucide-react";

// Roadmap (Ambiente Master) — acompanhamento da evolução do LOOTUS. Ajuste
// visual (delta 2026-09-21, referência visual fornecida pelo usuário) —
// SÓ o front muda: mesmos endpoints, mesmo modelo, mesma regra de
// progresso (sempre calculado pelo backend, nunca digitado). Blocos que a
// referência mostra mas o backend não tem (donut geral, progresso por
// versão, próximos marcos, atividade recente) são derivados 100% dos
// dados JÁ carregados (itens/itens_concluidos/itens_total/versao_prevista/
// previsao/created_at/updated_at) — nenhum endpoint novo.

const STATUS_LABEL = { planejado: "Planejado", em_andamento: "Em andamento", bloqueado: "Bloqueado", concluido: "Concluído" };
const STATUS_OPTIONS = Object.keys(STATUS_LABEL);
const STATUS_BADGE = {
  planejado: "bg-blue-100 text-blue-800", em_andamento: "bg-emerald-100 text-emerald-800",
  bloqueado: "bg-red-100 text-red-800", concluido: "bg-slate-200 text-slate-700",
};
const STATUS_DOT = { planejado: "bg-blue-500", em_andamento: "bg-emerald-500", bloqueado: "bg-red-500", concluido: "bg-slate-400" };
const FASE_LABEL = { planejamento: "Planejamento", desenvolvimento: "Desenvolvimento", homologacao: "Homologação", producao: "Produção", concluido: "Concluído" };
const FASE_OPTIONS = Object.keys(FASE_LABEL);
const FASE_ICON = { planejamento: FileText, desenvolvimento: Code2, homologacao: FlaskConical, producao: Rocket, concluido: CheckCircle2 };

// Paleta de cores do ícone de cada iniciativa — só variedade visual (a
// referência varia a cor por card), sem nenhum campo novo no backend:
// escolhida deterministicamente pela posição na lista, nunca aleatória
// (mesma iniciativa sempre com a mesma cor entre renders).
const ICON_PALETTE = [
  "bg-blue-100 text-blue-600", "bg-violet-100 text-violet-600", "bg-teal-100 text-teal-600",
  "bg-rose-100 text-rose-600", "bg-amber-100 text-amber-600", "bg-indigo-100 text-indigo-600",
];
const VERSION_PALETTE = ["bg-blue-500", "bg-emerald-500", "bg-rose-500", "bg-amber-500", "bg-indigo-500"];

const emptyForm = {
  titulo: "", descricao: "", prioridade: "", status: "planejado", fase: "planejamento",
  versao_prevista: "", responsavel: "", data_inicio: "", previsao: "",
  bloqueio_atual: "", proxima_acao: "",
};

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return "-";
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs)) return "-";
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr} hora${hr > 1 ? "s" : ""}`;
  const day = Math.floor(hr / 24);
  return `há ${day} dia${day > 1 ? "s" : ""}`;
}

function ProgressDonut({ percent, size = 96, stroke = 10 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, percent)) / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" className="text-slate-100" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" className="text-primary"
        strokeWidth={stroke} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.22} className="fill-slate-900 font-heading font-bold">
        {percent}%
      </text>
    </svg>
  );
}

function SummaryCard({ icon: Icon, colorClass, label, value, testId }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-heading font-bold text-slate-900" data-testid={testId}>{value}</div>
      </div>
    </Card>
  );
}

export default function Roadmap() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [novoItemTexto, setNovoItemTexto] = useState("");
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [q, setQ] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [prioridadeFiltro, setPrioridadeFiltro] = useState("todas");
  const [versaoFiltro, setVersaoFiltro] = useState("todas");
  const [responsavelFiltro, setResponsavelFiltro] = useState("todos");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/roadmap");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const prioridades = useMemo(() => Array.from(new Set(items.map(i => i.prioridade).filter(Boolean))).sort(), [items]);
  const versoes = useMemo(() => Array.from(new Set(items.map(i => i.versao_prevista).filter(Boolean))).sort(), [items]);
  const responsaveis = useMemo(() => Array.from(new Set(items.map(i => i.responsavel).filter(Boolean))).sort(), [items]);

  const stats = useMemo(() => ({
    planejado: items.filter(i => i.status === "planejado").length,
    em_andamento: items.filter(i => i.status === "em_andamento").length,
    bloqueado: items.filter(i => i.status === "bloqueado").length,
    concluido: items.filter(i => i.status === "concluido").length,
  }), [items]);

  const filteredItems = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter(i => {
      if (statusFiltro !== "todos" && i.status !== statusFiltro) return false;
      if (prioridadeFiltro !== "todas" && i.prioridade !== prioridadeFiltro) return false;
      if (versaoFiltro !== "todas" && (i.versao_prevista || "") !== versaoFiltro) return false;
      if (responsavelFiltro !== "todos" && (i.responsavel || "") !== responsavelFiltro) return false;
      if (!term) return true;
      return [i.titulo, i.descricao].filter(Boolean).join(" ").toLowerCase().includes(term);
    });
  }, [items, q, statusFiltro, prioridadeFiltro, versaoFiltro, responsavelFiltro]);

  const limparFiltros = () => {
    setQ(""); setStatusFiltro("todos"); setPrioridadeFiltro("todas"); setVersaoFiltro("todas"); setResponsavelFiltro("todos");
  };

  // --- Derivados 100% do que já está carregado — sem endpoint novo -------
  const overall = useMemo(() => {
    const totalItens = items.reduce((acc, i) => acc + (i.itens_total || 0), 0);
    const concluidos = items.reduce((acc, i) => acc + (i.itens_concluidos || 0), 0);
    const percent = totalItens > 0 ? Math.round((concluidos / totalItens) * 100) : 0;
    return { totalItens, concluidos, percent };
  }, [items]);

  const progressoPorVersao = useMemo(() => {
    const grupos = {};
    items.forEach(i => {
      if (!i.versao_prevista) return;
      const g = grupos[i.versao_prevista] || { versao: i.versao_prevista, total: 0, concluidos: 0 };
      g.total += i.itens_total || 0;
      g.concluidos += i.itens_concluidos || 0;
      grupos[i.versao_prevista] = g;
    });
    return Object.values(grupos)
      .sort((a, b) => a.versao.localeCompare(b.versao, "pt-BR", { numeric: true }))
      .map(g => ({ ...g, percent: g.total > 0 ? Math.round((g.concluidos / g.total) * 100) : 0 }));
  }, [items]);

  const proximosMarcos = useMemo(() => {
    return items
      .filter(i => i.previsao)
      .sort((a, b) => new Date(a.previsao) - new Date(b.previsao))
      .slice(0, 4);
  }, [items]);

  const atividadeRecente = useMemo(() => {
    const eventos = [];
    items.forEach(i => {
      if (i.created_at) eventos.push({ tipo: "create", titulo: i.titulo, at: i.created_at, status: i.status });
      if (i.updated_at && i.updated_at !== i.created_at) eventos.push({ tipo: "update", titulo: i.titulo, at: i.updated_at, status: i.status });
    });
    return eventos.sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 6);
  }, [items]);

  const exportCsv = () => {
    const linhas = [["Título", "Status", "Fase", "Prioridade", "Versão", "Responsável", "Itens concluídos", "Itens totais", "Progresso"]];
    filteredItems.forEach(i => {
      linhas.push([
        i.titulo, STATUS_LABEL[i.status], FASE_LABEL[i.fase], i.prioridade, i.versao_prevista || "",
        i.responsavel || "", i.itens_concluidos, i.itens_total, `${i.progresso}%`,
      ]);
    });
    const csv = linhas.map(l => l.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "roadmap.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setNovoItemTexto(""); setHistory([]); setHistoryOpen(false); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyForm, ...item }); setNovoItemTexto(""); setHistory([]); setHistoryOpen(false); setOpen(true); };

  const save = async () => {
    if (!form.titulo.trim()) { toast.error("Informe o título"); return; }
    if (!form.prioridade.trim()) { toast.error("Informe a prioridade"); return; }
    const body = {
      titulo: form.titulo.trim(), descricao: form.descricao, prioridade: form.prioridade.trim(),
      status: form.status, fase: form.fase,
      versao_prevista: form.versao_prevista || null, responsavel: form.responsavel || null,
      data_inicio: form.data_inicio || null, previsao: form.previsao || null,
      bloqueio_atual: form.bloqueio_atual || null, proxima_acao: form.proxima_acao || null,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/admin/roadmap/${editing.id}`, body);
        setItems(prev => prev.map(i => i.id === data.id ? data : i));
        setEditing(data);
        toast.success("Iniciativa atualizada.");
      } else {
        const { data } = await api.post("/admin/roadmap", body);
        setItems(prev => [...prev, data]);
        toast.success("Iniciativa criada.");
        setOpen(false);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addChecklistItem = async () => {
    const texto = novoItemTexto.trim();
    if (!texto || !editing) return;
    try {
      const { data } = await api.post(`/admin/roadmap/${editing.id}/itens`, { texto });
      setItems(prev => prev.map(i => i.id === data.id ? data : i));
      setEditing(data);
      setNovoItemTexto("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleChecklistItem = async (itemId, concluido) => {
    if (!editing) return;
    try {
      const { data } = await api.put(`/admin/roadmap/${editing.id}/itens/${itemId}`, { concluido });
      setItems(prev => prev.map(i => i.id === data.id ? data : i));
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeChecklistItem = async (itemId) => {
    if (!editing) return;
    try {
      const { data } = await api.delete(`/admin/roadmap/${editing.id}/itens/${itemId}`);
      setItems(prev => prev.map(i => i.id === data.id ? data : i));
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadHistory = async () => {
    if (!editing) return;
    try {
      const { data } = await api.get(`/admin/roadmap/${editing.id}/history`);
      setHistory(data);
      setHistoryOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      {/* 1) Cabeçalho */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Map size={28} /> Roadmap</h1>
          <p className="text-slate-500 text-sm mt-1">Acompanhe melhorias, fases e progresso do produto</p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={openNew} data-testid="roadmap-nova">
            <Plus size={16} /> Nova iniciativa
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={items.length === 0} data-testid="roadmap-exportar">
            <Download size={16} /> Exportar
          </Button>
        </div>
      </div>

      {/* 2) Cards de resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={FileText} colorClass="bg-blue-100 text-blue-600" label="Planejados" value={stats.planejado} testId="roadmap-card-planejado" />
        <SummaryCard icon={PlayCircle} colorClass="bg-teal-100 text-teal-600" label="Em andamento" value={stats.em_andamento} testId="roadmap-card-em_andamento" />
        <SummaryCard icon={AlertTriangle} colorClass="bg-rose-100 text-rose-600" label="Bloqueados" value={stats.bloqueado} testId="roadmap-card-bloqueado" />
        <SummaryCard icon={CheckCircle2} colorClass="bg-emerald-100 text-emerald-600" label="Concluídos" value={stats.concluido} testId="roadmap-card-concluido" />
      </div>

      {/* 3) Barra de filtros */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[150px]">
            <Label className="text-xs">Status</Label>
            <Select value={statusFiltro} onValueChange={setStatusFiltro}>
              <SelectTrigger data-testid="roadmap-filtro-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <Label className="text-xs">Versão</Label>
            <Select value={versaoFiltro} onValueChange={setVersaoFiltro}>
              <SelectTrigger data-testid="roadmap-filtro-versao"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {versoes.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <Label className="text-xs">Responsável</Label>
            <Select value={responsavelFiltro} onValueChange={setResponsavelFiltro}>
              <SelectTrigger data-testid="roadmap-filtro-responsavel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {responsaveis.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {prioridades.length > 0 && (
            <div className="min-w-[150px]">
              <Label className="text-xs">Prioridade</Label>
              <Select value={prioridadeFiltro} onValueChange={setPrioridadeFiltro}>
                <SelectTrigger data-testid="roadmap-filtro-prioridade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {prioridades.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Pesquisar</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar iniciativa..." className="pl-8" data-testid="roadmap-filtro-busca" />
            </div>
          </div>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="roadmap-filtrar" type="button">
            Filtrar
          </Button>
          <Button variant="ghost" size="icon" onClick={limparFiltros} title="Limpar filtros" data-testid="roadmap-limpar-filtros">
            <X size={16} />
          </Button>
        </div>
      </Card>

      {/* 4) Lista principal + 5) coluna lateral */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5 items-start">
        <div className="space-y-4">
          {loading && <Card className="p-8 text-center text-slate-400">Carregando...</Card>}
          {!loading && filteredItems.length === 0 && (
            <Card className="p-8 text-center text-slate-400">{items.length === 0 ? "Nenhuma iniciativa cadastrada" : "Nenhuma iniciativa para este filtro"}</Card>
          )}
          {filteredItems.map((item, idx) => {
            const FaseIcon = FASE_ICON[item.fase] || FileText;
            const iconClass = ICON_PALETTE[idx % ICON_PALETTE.length];
            const checklistPreview = (item.itens || []).slice(0, 3);
            const restantes = (item.itens || []).length - checklistPreview.length;
            return (
              <Card
                key={item.id} className="p-5 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openEdit(item)} data-testid={`roadmap-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-[240px]">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconClass}`}>
                      <FaseIcon size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-semibold text-slate-900">{item.titulo}</span>
                        <Badge className={STATUS_BADGE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
                        {item.versao_prevista
                          ? <Badge className="bg-slate-100 text-slate-700">{item.versao_prevista}</Badge>
                          : <Badge className="bg-slate-100 text-slate-700">{FASE_LABEL[item.fase]}</Badge>}
                      </div>
                      {item.descricao && <p className="text-sm text-slate-500 mt-1 max-w-md">{item.descricao}</p>}
                      {/* Delta Roadmap, item 1 — bloqueio/próxima ação em
                          destaque compacto, só quando a iniciativa está
                          bloqueada e tem algo preenchido. */}
                      {item.status === "bloqueado" && (item.bloqueio_atual || item.proxima_acao) && (
                        <div className="mt-2 max-w-md rounded-md border border-red-200 bg-red-50 p-2 text-xs space-y-1" data-testid={`roadmap-bloqueio-${item.id}`}>
                          {item.bloqueio_atual && (
                            <div className="flex items-start gap-1.5 text-red-800">
                              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                              <span><b>Bloqueio:</b> {item.bloqueio_atual}</span>
                            </div>
                          )}
                          {item.proxima_acao && (
                            <div className="flex items-start gap-1.5 text-red-700">
                              <ArrowRight size={12} className="mt-0.5 shrink-0" />
                              <span><b>Próxima ação:</b> {item.proxima_acao}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-6 ml-auto">
                    {checklistPreview.length > 0 && (
                      <div className="space-y-1 text-sm text-slate-600 min-w-[160px]">
                        {checklistPreview.map(it => (
                          <div key={it.id} className="flex items-center gap-1.5">
                            <Checkbox checked={it.concluido} disabled className="pointer-events-none" />
                            <span className={it.concluido ? "line-through text-slate-400" : ""}>{it.texto}</span>
                          </div>
                        ))}
                        {restantes > 0 && <div className="text-xs text-slate-400">+{restantes} item{restantes > 1 ? "s" : ""}</div>}
                      </div>
                    )}
                    <div className="text-right space-y-1 shrink-0">
                      {item.responsavel && (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-sm text-slate-600">{item.responsavel}</span>
                          <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-heading font-semibold shrink-0">
                            {initials(item.responsavel)}
                          </div>
                        </div>
                      )}
                      {item.previsao && (
                        <div className="flex items-center justify-end gap-1.5 text-sm text-slate-500">
                          <Calendar size={14} /> {fmtDate(item.previsao)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center gap-3">
                    <Progress value={item.progresso} className="flex-1" />
                    <span className="text-sm font-medium text-slate-700 w-10 text-right">{item.progresso}%</span>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-slate-500">{item.itens_concluidos} de {item.itens_total} itens concluídos</span>
                    <button
                      type="button" className="text-xs font-medium text-primary flex items-center gap-1 hover:underline"
                      onClick={(e) => { e.stopPropagation(); openEdit(item); }} data-testid={`roadmap-ver-detalhes-${item.id}`}
                    >
                      Ver detalhes <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* 5) Coluna lateral direita */}
        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-slate-900"><Gauge size={16} /> Resumo do progresso</div>
            <div className="flex items-center gap-4 mt-3">
              <ProgressDonut percent={overall.percent} />
              <div>
                <div className="font-heading font-bold text-slate-900">{overall.concluidos} de {overall.totalItens}</div>
                <div className="text-xs text-slate-500">entregas concluídas</div>
              </div>
            </div>
            {progressoPorVersao.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                <div className="text-xs font-medium text-slate-500">Progresso por versão</div>
                {progressoPorVersao.map((g, idx) => (
                  <div key={g.versao} className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${VERSION_PALETTE[idx % VERSION_PALETTE.length]}`} />
                    <span className="text-slate-600 w-10 shrink-0">{g.versao}</span>
                    <Progress value={g.percent} className="flex-1 h-1.5" />
                    <span className="text-slate-500 text-xs w-9 text-right shrink-0">{g.percent}%</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-slate-900"><Flag size={16} /> Próximos marcos</div>
            <div className="mt-3 space-y-2.5">
              {proximosMarcos.length === 0 && <div className="text-xs text-slate-400">Nenhum marco com previsão definida ainda.</div>}
              {proximosMarcos.map(m => (
                <div key={m.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`roadmap-marco-${m.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[m.status]}`} />
                    <span className="text-slate-700 truncate">{m.titulo}</span>
                  </div>
                  <span className="text-xs text-slate-500 shrink-0">{fmtDate(m.previsao)}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-slate-900"><Activity size={16} /> Atividade recente</div>
            <div className="mt-3 space-y-2.5">
              {atividadeRecente.length === 0 && <div className="text-xs text-slate-400">Nenhuma atividade registrada ainda.</div>}
              {atividadeRecente.map((ev, idx) => (
                <div key={idx} className="flex items-start justify-between gap-2 text-sm">
                  <div className="flex items-start gap-2 min-w-0">
                    {ev.tipo === "create"
                      ? <Plus size={14} className="text-blue-500 mt-0.5 shrink-0" />
                      : ev.status === "concluido"
                        ? <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                        : ev.status === "bloqueado"
                          ? <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
                          : <ListChecks size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                    <span className="text-slate-600 truncate">
                      {ev.tipo === "create" ? "Iniciativa criada: " : "Atualizada: "}{ev.titulo}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">{timeAgo(ev.at)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* 6) Tabela resumida */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 font-heading font-semibold border-b border-slate-100">Itens do roadmap</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="py-2.5 px-4">Iniciativa</th><th>Fase</th><th>Responsável</th><th>Status</th><th className="pr-4">Progresso</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(i => (
                <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openEdit(i)}>
                  <td className="py-2.5 px-4 font-medium text-slate-900">{i.titulo}</td>
                  <td className="text-slate-600">{FASE_LABEL[i.fase]}</td>
                  <td className="text-slate-600">
                    {i.responsavel ? (
                      <span className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-heading font-semibold">{initials(i.responsavel)}</span>
                        {i.responsavel}
                      </span>
                    ) : <span className="text-slate-400">-</span>}
                  </td>
                  <td><Badge className={STATUS_BADGE[i.status]}>{STATUS_LABEL[i.status]}</Badge></td>
                  <td className="pr-4">
                    <div className="flex items-center gap-2 max-w-[160px]">
                      <Progress value={i.progresso} className="flex-1 h-1.5" />
                      <span className="text-xs text-slate-500 w-9 text-right shrink-0">{i.progresso}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredItems.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">Nenhuma iniciativa para este filtro</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar iniciativa" : "Nova iniciativa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} data-testid="roadmap-form-titulo" /></div>
            <div><Label>Descrição</Label><Textarea value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} data-testid="roadmap-form-descricao" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prioridade</Label><Input value={form.prioridade} onChange={e => setForm({ ...form, prioridade: e.target.value })} placeholder="Ex.: Alta" data-testid="roadmap-form-prioridade" /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="roadmap-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fase</Label>
                <Select value={form.fase} onValueChange={v => setForm({ ...form, fase: v })}>
                  <SelectTrigger data-testid="roadmap-form-fase"><SelectValue /></SelectTrigger>
                  <SelectContent>{FASE_OPTIONS.map(f => <SelectItem key={f} value={f}>{FASE_LABEL[f]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Versão prevista</Label><Input value={form.versao_prevista || ""} onChange={e => setForm({ ...form, versao_prevista: e.target.value })} data-testid="roadmap-form-versao" /></div>
              <div><Label>Responsável</Label><Input value={form.responsavel || ""} onChange={e => setForm({ ...form, responsavel: e.target.value })} data-testid="roadmap-form-responsavel" /></div>
              <div><Label>Data de início</Label><Input type="date" value={form.data_inicio || ""} onChange={e => setForm({ ...form, data_inicio: e.target.value })} data-testid="roadmap-form-data-inicio" /></div>
              <div><Label>Previsão</Label><Input type="date" value={form.previsao || ""} onChange={e => setForm({ ...form, previsao: e.target.value })} data-testid="roadmap-form-previsao" /></div>
            </div>

            {/* Delta Roadmap, item 1 — só aparece quando Status = Bloqueado
                (nunca obrigatório, nunca aparece para os outros status). */}
            {form.status === "bloqueado" && (
              <div className="grid grid-cols-1 gap-3 rounded-md border border-red-200 bg-red-50 p-3" data-testid="roadmap-form-bloqueio-secao">
                <div>
                  <Label>Bloqueio atual</Label>
                  <Textarea value={form.bloqueio_atual || ""} onChange={e => setForm({ ...form, bloqueio_atual: e.target.value })}
                    placeholder="O que está impedindo o avanço desta iniciativa?" data-testid="roadmap-form-bloqueio-atual" />
                </div>
                <div>
                  <Label>Próxima ação</Label>
                  <Textarea value={form.proxima_acao || ""} onChange={e => setForm({ ...form, proxima_acao: e.target.value })}
                    placeholder="Qual é o próximo passo para destravar?" data-testid="roadmap-form-proxima-acao" />
                </div>
              </div>
            )}

            {editing && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <Label>Checklist — {editing.itens_concluidos} de {editing.itens_total} concluídos ({editing.progresso}%)</Label>
                  <Button variant="ghost" size="sm" onClick={loadHistory} data-testid="roadmap-historico-btn"><History size={14} /> Histórico</Button>
                </div>
                <Progress value={editing.progresso} />
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(editing.itens || []).map(it => (
                    <div key={it.id} className="flex items-center gap-2 text-sm" data-testid={`roadmap-checklist-item-${it.id}`}>
                      <Checkbox checked={it.concluido} onCheckedChange={v => toggleChecklistItem(it.id, !!v)} data-testid={`roadmap-checklist-toggle-${it.id}`} />
                      <span className={it.concluido ? "line-through text-slate-400 flex-1" : "flex-1"}>{it.texto}</span>
                      <Button size="sm" variant="ghost" onClick={() => removeChecklistItem(it.id)} data-testid={`roadmap-checklist-remove-${it.id}`}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                  {(!editing.itens || editing.itens.length === 0) && <div className="text-xs text-slate-400">Nenhum item no checklist ainda.</div>}
                </div>
                <div className="flex gap-2">
                  <Input value={novoItemTexto} onChange={e => setNovoItemTexto(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addChecklistItem(); } }}
                    placeholder="Novo item do checklist" data-testid="roadmap-checklist-novo-texto" />
                  <Button variant="outline" onClick={addChecklistItem} data-testid="roadmap-checklist-adicionar"><Plus size={14} /></Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
            <Button onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="roadmap-form-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Histórico — {editing?.titulo}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {history.length === 0 && <div className="text-sm text-slate-400">Nenhum evento registrado ainda.</div>}
            {history.map((h, idx) => (
              <div key={idx} className="text-sm border-b border-slate-100 pb-2">
                <div className="font-medium">{h.op}</div>
                <div className="text-xs text-slate-500">{h.user_name || "-"} — {h.at ? new Date(h.at).toLocaleString("pt-BR") : "-"}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
